import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';

import { Field, Form, Formik } from 'formik';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Flex,
  Box,
  Input,
  Select,
  Textarea,
  RadioGroup,
  Stack,
  Radio,
} from '@chakra-ui/react';

import {
  updateCourseAction,
  fetchCourseCategoriesAction,
} from 'store/modules/course/course-actions';
import { courseActions } from 'store/modules/course/course-slice';

import { ReducerStatus } from 'utils/constants';

import useFileInput from 'hooks/useFileInput';
import useTagsInput from 'hooks/useTagsInput';

import ResultModal from 'components/common/modals/ResultModal';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import ImageUploadInput from 'components/common/forms/ImageUploadInput';
import MultiWordInput from 'components/common/forms/MultiWordInput';
import MultiEmailInput from 'components/common/forms/MultiEmailInput';

function UpdateCourseMd() {
  // Before using this component, you must have a value from modules/course/course state
  const dispatch = useDispatch();

  const categories = useSelector((state) => state.course.categories);
  const updateCourse = useSelector((state) => state.course.updateCourse);
  const course = useSelector((state) => state.course.course);

  const [updateFormInputs, setUpdateFormInputs] = useState({
    name: '',
    description: '',
    code: '',
    categoryId: '',
  });

  const [isPublished, setIsPublished] = useState('false');

  const [isPrivate, setIsPrivate] = useState('false');

  const courseFormValidation = Yup.object({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().max(1000).required('Required'),
    categoryId: Yup.string().required('Required'),
    code: Yup.string().min(1).max(20).required('Required'),
  });

  const {
    file,
    fileURL,
    fileError,
    fileInputRef,
    setFile,
    handleAttachFile,
    handleRemoveSelectedFile,
    handleFileInputChange,
  } = useFileInput({ maxFileSizeMb: 5, fileType: 'image/' });

  const {
    tags,
    inputTags,
    setTags,
    handleTagsChange,
    handleTagRemove,
    handleTagAdd,
    handleTagKeyDown,
  } = useTagsInput();

  // State for managing selected users/groups in MultiEmailInput
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectedTargetEmails, setSelectedTargetEmails] = useState([]);

  /**
   * Handles adding a new item to the owner groups list
   */
  const handleEmailAdd = (item) => {
    setSelectedEmails((prev) => [...prev, item]);
  };

  /**
   * Handles removing an item from the owner groups list
   */
  const handleEmailRemove = (item) => {
    setSelectedEmails((prev) =>
      prev.filter(
        (email) => email.value !== item.value || email.type !== item.type
      )
    );
  };

  /**
   * Handles adding a new item to the target groups list
   */
  const handleTargetEmailAdd = (item) => {
    setSelectedTargetEmails((prev) => [...prev, item]);
  };

  /**
   * Handles removing an item from the target groups list
   */
  const handleTargetEmailRemove = (item) => {
    setSelectedTargetEmails((prev) =>
      prev.filter(
        (email) => email.value !== item.value || email.type !== item.type
      )
    );
  };

  useEffect(() => {
    const resetForm = () => {
      setUpdateFormInputs({
        name: '',
        description: '',
        code: '',
        categoryId: '',
      });

      setFile({});
      setTags([]);
      setSelectedEmails([]);
      setSelectedTargetEmails([]);
    };

    if (updateCourse.modal) {
      dispatch(fetchCourseCategoriesAction());
      resetForm();

      setUpdateFormInputs({
        name: course?.name,
        code:
          course?.code !== null && course?.code !== undefined
            ? course?.code
            : '',
        description: course?.description,
        categoryId: course?.category?.id,
      });

      setIsPublished(course?.isPublished.toString());

      setIsPrivate(course?.isPrivate.toString());

      const parsedTags = JSON.parse(course?.tags);

      if (
        Array.isArray(parsedTags) &&
        parsedTags.every((tag) => typeof tag === 'string')
      ) {
        setTags(parsedTags);
      }

      // Convert existing emails format to MultiEmailInput format
      const parsedEmails = JSON.parse(course?.ownerGroups);
      if (
        Array.isArray(parsedEmails) &&
        parsedEmails.every((group) => typeof group === 'string')
      ) {
        const convertedEmails = parsedEmails.map((email) => ({
          label: email,
          value: email,
          type: 'groups',
          data: { name: email, mail: email, email },
        }));
        setSelectedEmails(convertedEmails);
      }

      const parsedTargetEmails = JSON.parse(course?.targetGroups);
      if (
        Array.isArray(parsedTargetEmails) &&
        parsedTargetEmails.every((group) => typeof group === 'string')
      ) {
        const convertedTargetEmails = parsedTargetEmails.map((email) => ({
          label: email,
          value: email,
          type: 'groups',
          data: { name: email, mail: email, email },
        }));
        setSelectedTargetEmails(convertedTargetEmails);
      }
    }
  }, [updateCourse.modal, course, setTags, setFile, dispatch]);

  const handleToggleUpdateModal = (isOpen) => {
    dispatch(courseActions.toggleUpdateModal(isOpen));
  };

  const resetForm = () => {
    setUpdateFormInputs({
      name: '',
      description: '',
      code: '',
      categoryId: '',
    });

    handleRemoveSelectedFile();
    setTags([]);
    setSelectedEmails([]);
  };

  const handleResultButton = () => {
    dispatch(courseActions.resetUpdateStatus());
    if (updateCourse.status === ReducerStatus.SUCCEEDED) {
      resetForm();
      handleToggleUpdateModal(false);
    }
  };

  const handlePublishStatusChange = (e) => {
    setIsPublished(e);
  };

  const handleIsPrivateChange = (e) => {
    setIsPrivate(e);
    if (e === 'false') {
      setSelectedTargetEmails([]);
    }
  };

  const handleUpdateCourse = (formikObject) => {
    const formData = new FormData();
    Object.entries(formikObject).forEach(([key, value]) => {
      setUpdateFormInputs((prevValues) => ({
        ...prevValues,
        [key]: value,
      }));
      formData.append(key, value);
    });
    tags.forEach((tag) => formData.append('Tags', tag));

    // Convert selectedEmails to simple email strings for backend compatibility
    const emails = selectedEmails.map((item) => item.value);
    emails.forEach((group) => formData.append('OwnerGroups', group));

    const targetEmails = selectedTargetEmails.map((item) => item.value);
    targetEmails.forEach((group) => formData.append('TargetGroups', group));

    if (file && file.name) {
      formData.append('Thumbnail', file, file.name);
    }

    formData.append('IsPublished', isPublished);

    formData.append('IsPrivate', isPrivate);

    if (course?.thumbnail && file?.name) {
      formData.append('IsRemoveThumbnail', 'true');
    }

    dispatch(updateCourseAction({ id: course?.id, formData, resetForm }));
  };
  return (
    <Box>
      {updateCourse.status === ReducerStatus.SUCCEEDED ||
      updateCourse.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={updateCourse.modal}
          onSubmit={handleResultButton}
          isError={updateCourse.status === ReducerStatus.FAILED}
          title={
            updateCourse.status === ReducerStatus.SUCCEEDED
              ? 'Course Updated'
              : null
          }
          message={
            updateCourse.status === ReducerStatus.SUCCEEDED
              ? 'The course has been updated successfully.'
              : updateCourse.error?.data?.title || 'Server Error'
          }
          errorObject={updateCourse.error?.data?.errors}
        />
      ) : (
        <Modal
          size="2xl"
          isOpen={updateCourse.modal}
          onClose={() => handleToggleUpdateModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Course</ModalHeader>
            <ModalCloseButton />

            <Formik
              enableReinitialize
              initialValues={updateFormInputs}
              onSubmit={(values) => {
                handleUpdateCourse(values);
              }}
              validationSchema={courseFormValidation}
            >
              {() => (
                <Form>
                  <ModalBody my={3}>
                    <VStack spacing={6} align="stretch">
                      <Flex
                        gap={5}
                        direction={{
                          base: 'column',
                          md: 'row',
                        }}
                      >
                        <Field flex={1} name="name">
                          {({ field, form }) => (
                            <FormikFormControl
                              label="Name"
                              helperText="Enter the name of the course. Make it concise and descriptive."
                              errors={form.errors.name}
                              form={form}
                              touched={form.touched.name}
                            >
                              <Input {...field} />
                            </FormikFormControl>
                          )}
                        </Field>

                        <Box
                          w={{
                            base: '100%',
                            md: '380px',
                          }}
                        >
                          <Field name="code">
                            {({ field, form }) => (
                              <FormikFormControl
                                label="Course Code"
                                helperText=" This code will be used to distinguish the course from others."
                                errors={form.errors.code}
                                touched={form.touched.code}
                              >
                                <Input {...field} />
                              </FormikFormControl>
                            )}
                          </Field>
                        </Box>
                      </Flex>

                      <Field name="description">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Description"
                            helperText="Provide a detailed description of the course. Mention the key objectives and what learners can expect to gain."
                            errors={form.errors.description}
                            touched={form.touched.description}
                          >
                            <Textarea {...field} />
                          </FormikFormControl>
                        )}
                      </Field>

                      <ImageUploadInput
                        label="Thumbnail"
                        helperText="Attach a visually appealing image that represents the course. File type should be an image, and the size should not exceed 5MB. The recommended aspect ratio for the thumbnail is 16:9."
                        currentImage={course?.thumbnail}
                        file={file}
                        fileURL={fileURL}
                        fileError={fileError}
                        fileInputRef={fileInputRef}
                        handleRemoveSelectedFile={handleRemoveSelectedFile}
                        handleAttachFile={handleAttachFile}
                        handleFileInputChange={handleFileInputChange}
                      />

                      <Field name="categoryId">
                        {({ field, form }) => (
                          <FormikFormControl
                            label="Category"
                            helperText="Choose the appropriate category for the course. This helps in organizing and searching for courses later."
                            errors={form.errors.categoryId}
                            touched={form.touched.categoryId}
                          >
                            <Select {...field} placeholder="Select category">
                              {categories.length > 0 &&
                                categories.map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))}
                            </Select>
                          </FormikFormControl>
                        )}
                      </Field>

                      <MultiWordInput
                        label="Tags"
                        helperText="Enter relevant tags and press Enter after each one to add to the list. Tags help in categorizing and searching for courses. Example: 'programming, web development, beginner.'"
                        newWordKeyName="newTag"
                        newWord={inputTags.newTag}
                        words={tags}
                        isWordError={inputTags.isTagError}
                        errorMessage={inputTags.errorMessage}
                        onWordChange={handleTagsChange}
                        onWordAdd={handleTagAdd}
                        onWordKeyDown={handleTagKeyDown}
                        onWordRemove={handleTagRemove}
                      />

                      <MultiEmailInput
                        label="Owner Groups"
                        helperText="Search and select users or distribution groups that have the authority to modify this course. You can switch between searching users and groups using the dropdown."
                        selectedItems={selectedEmails}
                        isError={false}
                        errorMessage=""
                        onItemAdd={handleEmailAdd}
                        onItemRemove={handleEmailRemove}
                        defaultSearchType="groups"
                      />

                      <FormikFormControl
                        label="Published Status"
                        helperText="Choose between 'Draft' if the course is still in progress and not ready for public access, or 'Published' if the course is ready for learners."
                      >
                        <RadioGroup
                          value={isPublished}
                          onChange={handlePublishStatusChange}
                        >
                          <Stack spacing={5} direction="row">
                            <Radio colorScheme="purple" value="false">
                              Draft
                            </Radio>
                            <Radio colorScheme="green" value="true">
                              Published
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      </FormikFormControl>

                      <FormikFormControl
                        label="Private Access"
                        helperText="Select 'Yes' for Private Access if the course will be accessed privately, and input the email addresses of the authorized users. You can use this option, especially if you want the course to be accessed by a specific class."
                      >
                        <RadioGroup
                          value={isPrivate}
                          onChange={handleIsPrivateChange}
                        >
                          <Stack spacing={5} direction="row">
                            <Radio colorScheme="red" value="true">
                              Yes
                            </Radio>
                            <Radio colorScheme="red" value="false">
                              No
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      </FormikFormControl>
                    </VStack>

                    {isPrivate === 'true' && (
                      <Box mt={4}>
                        <MultiEmailInput
                          label="Target Groups"
                          helperText="Search and select users or distribution groups that have the authority to access this course. You can switch between searching users and groups using the dropdown."
                          selectedItems={selectedTargetEmails}
                          isError={false}
                          errorMessage=""
                          onItemAdd={handleTargetEmailAdd}
                          onItemRemove={handleTargetEmailRemove}
                          defaultSearchType="groups"
                        />
                      </Box>
                    )}
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      mr={3}
                      onClick={() => handleToggleUpdateModal(false)}
                    >
                      Close
                    </Button>
                    <Button
                      colorScheme="red"
                      px={10}
                      type="submit"
                      isLoading={updateCourse.status === ReducerStatus.LOADING}
                    >
                      Update
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default UpdateCourseMd;
